import { useState } from "react";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TrustBannerSection from "@/components/feature/TrustBannerSection";

export default function ContactoPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      const response = await fetch("https://formspree.io/f/xojzbvwg", {
        method: "POST",
        body: (() => { const p = new URLSearchParams(data as unknown as Record<string, string>); p.append("formulario","Contacto"); p.append("_subject","Contacto — Persianas Málaga"); return p; })(),
        headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json" },
      });
      if (response.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("idle");
      }
    } catch {
      setFormStatus("idle");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="Contacto | Persianas Málaga | 951 12 07 70"
        description="Contacta con Persianas Málaga: teléfono 951 12 07 70, WhatsApp, formulario o email. Presupuesto gratis de reparación de persianas en Málaga capital y 16 barrios."
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Contacto" },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Persianas Málaga",
        "description": "Contacto de Persianas Málaga. Teléfono 951 12 07 70 y WhatsApp para reparación de persianas, mosquiteras y motorización en Málaga capital y 16 barrios.",
        "telephone": "+34 951 12 07 70",
        "email": "info@persianas-malaga.es",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Málaga",
          "addressRegion": "Andalucía",
          "addressCountry": "ES"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "19:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "09:00",
            "closes": "14:00"
          }
        ]
      }) }} />

      {/* Hero */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 font-heading">
            Contacta con <span className="text-primary-500">nosotros</span>
          </h1>
          <p className="text-sm md:text-lg text-foreground-600 mb-8 max-w-lg mx-auto">
            Estamos a tu disposición en Málaga capital. Llámanos, escríbenos por WhatsApp o rellena el formulario. Te responderemos en menos de 15 minutos.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <div className="bg-background-100 rounded-xl p-5 flex items-start gap-3 text-left">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 flex-shrink-0">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-primary-600"></i></span>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Teléfono</p>
                <a href="tel:+34951120770" className="text-lg font-bold text-foreground-950 hover:text-primary-500 transition-colors cursor-pointer">951 12 07 70</a>
                <p className="text-xs text-foreground-600 mt-0.5">Lun-Vie: 8:00-19:00 · Sáb: 9:00-14:00</p>
              </div>
            </div>
            <div className="bg-background-100 rounded-xl p-5 flex items-start gap-3 text-left">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366]/10 flex-shrink-0">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-fill text-[#25D366]"></i></span>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">WhatsApp</p>
                <a href="https://wa.me/34627213347" target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-foreground-950 hover:text-[#25D366] transition-colors cursor-pointer">Enviar mensaje</a>
                <p className="text-xs text-foreground-600 mt-0.5">Respuesta en menos de 15 minutos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            {/* Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">Solicita presupuesto</h2>
              {formStatus === "success" ? (
                <div className="bg-background-50 rounded-2xl p-8 text-center border border-background-200/60">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <span className="w-7 h-7 flex items-center justify-center"><i className="ri-check-line text-primary-600 text-2xl"></i></span>
                  </div>
                  <p className="text-lg font-bold text-foreground-900 mb-1 font-heading">¡Mensaje enviado!</p>
                  <p className="text-sm text-foreground-600 mb-4">Gracias por contactar con Persianas Málaga. Te responderemos en menos de 15 minutos.</p>
                  <button onClick={() => setFormStatus("idle")} className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Enviar otra solicitud</button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="bg-background-50 rounded-2xl p-6 md:p-8 space-y-4 border border-background-200/60">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">Nombre *</label>
                    <input id="contact-name" name="name" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400" placeholder="Tu nombre completo" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">Teléfono *</label>
                    <input id="contact-phone" name="phone" type="tel" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400" placeholder="Tu teléfono" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">Email</label>
                    <input id="contact-email" name="email" type="email" className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400" placeholder="tu@email.com" />
                  </div>
                  <div>
                    <label htmlFor="contact-barrio" className="block text-sm font-medium text-foreground-800 mb-1.5">Barrio</label>
                    <select id="contact-barrio" name="barrio" className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 appearance-none cursor-pointer">
                      <option value="">Selecciona tu barrio</option>
                      <option value="Centro Histórico">Centro Histórico</option>
                      <option value="La Malagueta">La Malagueta</option>
                      <option value="El Palo">El Palo</option>
                      <option value="Pedregalejo">Pedregalejo</option>
                      <option value="Teatinos">Teatinos</option>
                      <option value="El Limonar">El Limonar</option>
                      <option value="Churriana">Churriana</option>
                      <option value="Carretera de Cádiz">Carretera de Cádiz</option>
                      <option value="Cruz de Humilladero">Cruz de Humilladero</option>
                      <option value="Bailén-Miraflores">Bailén-Miraflores</option>
                      <option value="Ciudad Jardín">Ciudad Jardín</option>
                      <option value="Puerto de la Torre">Puerto de la Torre</option>
                      <option value="Campanillas">Campanillas</option>
                      <option value="Huelin">Huelin</option>
                      <option value="Soliva">Soliva</option>
                      <option value="El Cónsul">El Cónsul</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="block text-sm font-medium text-foreground-800 mb-1.5">Servicio *</label>
                    <select id="contact-service" name="service" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 appearance-none cursor-pointer">
                      <option value="">Selecciona un servicio</option>
                      <option value="Persianas">Persianas</option>
                      <option value="Mosquiteras">Mosquiteras</option>
                      <option value="Motorización">Motorización</option>
                      <option value="Varios servicios">Varios servicios</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">Mensaje</label>
                    <textarea id="contact-message" name="message" rows={4} maxLength={500} className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 resize-none" placeholder="Cuéntanos qué necesitas..."></textarea>
                    <p className="text-xs text-foreground-400 mt-1">Máximo 500 caracteres</p>
                  </div>
                  <button type="submit" disabled={formStatus === "submitting"} className="w-full py-3.5 px-6 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-primary-300 disabled:to-primary-300 text-background-50 font-semibold text-sm rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20">
                    {formStatus === "submitting" ? "Enviando..." : "Enviar solicitud"}
                  </button>
                </form>
              )}
            </div>

            {/* Map + Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">Zona de cobertura</h2>
              <div className="w-full rounded-2xl overflow-hidden mb-6 border border-background-200/60">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d102347.428765434!2d-4.487518624414005!3d36.72015851603788!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd72f4e1f3b3b3b3%3A0x8e2e8c8c8c8c8c8c!2sM%C3%A1laga%2C+Spain!5e0!3m2!1ses!2ses!4v1718400000000"
                  width="100%"
                  height="350"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Zona de cobertura de Persianas Málaga - Málaga capital y 16 barrios"
                  className="w-full h-[350px]"
                />
              </div>
              <div className="bg-background-50 rounded-2xl p-6 border border-background-200/60">
                <h3 className="text-lg font-bold text-foreground-950 mb-4 font-heading">Información de contacto</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0 mt-0.5"><i className="ri-map-pin-2-fill"></i></span>
                    <span className="text-foreground-700"><strong>Zona de servicio:</strong> Málaga capital y sus 16 barrios (Centro Histórico, La Malagueta, El Palo, Pedregalejo, Teatinos, El Limonar, Churriana, Carretera de Cádiz, Cruz de Humilladero, Bailén-Miraflores, Ciudad Jardín, Puerto de la Torre, Campanillas, Huelin, Soliva y El Cónsul)</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0 mt-0.5"><i className="ri-phone-fill"></i></span>
                    <span className="text-foreground-700"><strong>Teléfono:</strong> 951 12 07 70</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0 mt-0.5"><i className="ri-mail-fill"></i></span>
                    <span className="text-foreground-700"><strong>Email:</strong> info@persianas-malaga.es</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 flex-shrink-0 mt-0.5"><i className="ri-time-line"></i></span>
                    <span className="text-foreground-700"><strong>Horario:</strong> Lunes a Viernes 8:00 – 19:00 · Sábados 9:00 – 14:00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}